import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, Check } from "lucide-react";
import { STATS, RECURRING_PLANS } from "@/data/solutions";
import { IMAGES } from "@/lib/brand";
import AnimatedCounter from "@/components/AnimatedCounter";
import Methodology from "@/components/Methodology";

export default function NumbersSection() {
  return (
    <section id="numeros" className="scroll-mt-nav relative bg-efory-ink text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.numbers} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-efory-ink via-efory-ink/90 to-efory-ink" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-efory-blue/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
            <TrendingUp className="h-3.5 w-3.5" /> Resultados que falam por nós
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-6 leading-[1.08]">
            Números, controle e{" "}
            <span className="text-gradient-blue">receita recorrente</span>
          </h2>
          <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed">
            Não dá para ter um comercial forte sem controle financeiro. Construímos previsibilidade
            de receita para o seu negócio crescer com segurança.
          </p>
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`stat-${i}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8 hover:border-efory-blue/40 transition-colors duration-300"
            >
              <div className="font-heading text-3xl md:text-4xl font-extrabold text-efory-blue">
                <AnimatedCounter
                  to={s.to}
                  decimals={s.decimals || 0}
                  prefix={s.prefix || ""}
                  suffix={s.suffix || ""}
                />
              </div>
              <div className="mt-2 text-sm text-white/60 leading-snug">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Methodology + systems */}
        <div className="mt-16 grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Left: validated methodology */}
          <div className="lg:col-span-6 flex">
            <div className="w-full">
              <Methodology />
            </div>
          </div>

          {/* Right: proprietary systems side-by-side, filling methodology height */}
          <div className="lg:col-span-6">
            <div className="grid sm:grid-cols-2 gap-6 h-full">
              {RECURRING_PLANS.map((p, idx) => {
                const isGreen = p.color === "green";
                return (
                  <motion.div
                    key={p.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    data-testid={`plan-${idx}`}
                    className={`h-full rounded-3xl p-7 border transition-all duration-300 flex flex-col ${
                      p.highlight
                        ? isGreen
                          ? "bg-white/[0.03] border-efory-green/50 hover:border-efory-green hover:-translate-y-1"
                          : "bg-efory-blue text-white border-efory-blue shadow-xl shadow-efory-blue/25"
                        : "bg-white/[0.03] border-white/10 hover:border-efory-blue/40 hover:-translate-y-1"
                    }`}
                    style={
                      isGreen
                        ? {
                            background:
                              "radial-gradient(circle at 50% 0%, rgba(34,197,94,0.15), rgba(255,255,255,0.03) 60%)",
                          }
                        : undefined
                    }
                  >
                    <span
                      className={`inline-block self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-4 ${
                        p.highlight
                          ? isGreen
                            ? "bg-efory-green/15 text-efory-green"
                            : "bg-white/20"
                          : "bg-efory-blue/10 text-efory-blue"
                      }`}
                    >
                      Sistema próprio
                    </span>
                    <h4 className="font-heading text-lg font-bold leading-tight text-white">{p.name}</h4>
                    <p className={`mt-3 text-sm leading-relaxed ${p.highlight && !isGreen ? "text-white/85" : "text-white/60"}`}>
                      {p.desc}
                    </p>
                    <ul className="mt-5 space-y-2.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-center gap-2.5 text-sm">
                          <Check
                            className={`h-4 w-4 shrink-0 ${
                              p.highlight
                                ? isGreen
                                  ? "text-efory-green"
                                  : "text-white"
                                : "text-efory-blue"
                            }`}
                          />
                          <span className={p.highlight && !isGreen ? "text-white/90" : "text-white/70"}>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
